import { useCallback, useEffect, useRef } from 'react';
import { useScript } from 'usehooks-ts';

import { Codeowners } from '../../consts/codeowners';
import { useOnlineBookingReducer } from '../../state/useOnlineBookingReducer';
import { captureException } from '../../utils/exception';

type RecaptchaType = {
  enterprise: {
    execute: (token: string, options: { action: string }) => Promise<string | null>;
  };
};

declare const window: {
  grecaptcha: RecaptchaType;
} & Window;

export const useRecaptcha = () => {
  const recaptchaRef = useRef<RecaptchaType>();
  const { state } = useOnlineBookingReducer();
  const recaptchaUrl = `https://www.google.com/recaptcha/enterprise.js?render=${window.RECAPTCHA_ENTERPRISE_KEY}`;

  const shouldPreventLoad = state.appOpened === false || state.initParams?.source === 'customer_portal';

  const status = useScript(recaptchaUrl, {
    shouldPreventLoad,
  });
  useEffect(() => {
    if (status === 'ready' && window.grecaptcha) {
      recaptchaRef.current = window.grecaptcha;
    }
  }, [status]);

  const getRecaptchaToken = useCallback(async (action: string): Promise<string | null> => {
    if (recaptchaRef.current) {
      try {
        return recaptchaRef.current.enterprise.execute(window.RECAPTCHA_ENTERPRISE_KEY, { action });
      } catch (error) {
        captureException(error, Codeowners.CUSTOMER_INTAKE);
        return Promise.resolve(null);
      }
    }
    return null;
  }, []);

  return {
    getRecaptchaToken,
  };
};
